import axios from "axios";
import {
  SnowTypeEntry,
  AvalancheTypeEntry,
  ImageTypeEntry,
} from "../types/report";

interface ReportData {
  title: string;
  validFrom: string;
  validUntil: string;
  regions: string[];
  snowEntries: SnowTypeEntry[];
  avaEntries: AvalancheTypeEntry[];
  markdownText: string;
  imageEntries: ImageTypeEntry[];
  avalancheSituation: string;
}

const avaTypeMap: Record<string, string> = {
  newsnow: "Neuschnee",
  windslab: "Triebschnee",
  glidesnow: "Gleitschnee",
  weaklayer: "Altschnee",
  wetsnow: "Nassschnee",
};

const mapAvaType = (displayName: string): string => {
  return avaTypeMap[displayName] ?? displayName.toLowerCase();
};

const avaSituationMap: Record<string, string> = {
  keiner: "Keine Angabe",
  leicht: "Einfache Lawinensituation",
  schwierig: "Schwierige Lawinensituation",
  kompliziert: "Komplexe Lawinensituation",
};

const avaSituation = (displayName: string): string => {
  if (displayName == null) {
    return "Keine Angabe";
  }
  return avaSituationMap[displayName] ?? displayName.toLowerCase();
};

const snowTypeMap: Record<string, string> = {
  pulverschnee: "Pulverschnee",
  firn: "Firn",
  harschdeckel: "Tragfähiger Harschdeckel",
  bruchharsch: "Bruchharsch",
  faulschnee: "Faulschnee",
  vereist: "Vereist",
  sommerschnee: "Sommerschnee",
  noppenpulver: "Noppenpulver",
  windbeeinflusst: "Windbeeinflusst",
  wechselhafterschnee: "Wechselhafter Schnee",
  nassschnee: "Nassschnee",
  aper: "Aper",
};

const mapSnowType = (input: string): string => {
  return snowTypeMap[input.toLowerCase()] ?? input;
};

export async function loadReport(id: string): Promise<ReportData | null> {
  try {
    const response = await axios.get(
      `https://backend.schnee.report/api/reports?filters[documentId][$eq]=${id}&status=draft&populate[images][populate]=picture&populate[snow][populate]=expos&populate[avalanche][populate]=expos&populate[regions]=true&populate[snow]=true&populate[avalanche]=true`
    );

    if (!response.data.data || response.data.data.length === 0) {
      return null;
    }

    const reportData = response.data.data[0];

    // Transform the API response to match the state structure in ReportCreate
    return {
      title: reportData.title || "",
      validFrom: reportData.valid_from
        ? new Date(reportData.valid_from).toLocaleDateString("sv-SE")
        : "",
      validUntil: reportData.valid_until
        ? new Date(reportData.valid_until).toLocaleDateString("sv-SE")
        : "",
      regions:
        reportData.regions?.map((region: any) => region.documentId) || [],
      snowEntries: transformSnowEntries(reportData.snow),
      avaEntries: transformAvalancheEntries(reportData.avalanche),
      markdownText: reportData.content || "",
      imageEntries: await transformImageEntries(reportData.images),
      avalancheSituation:
        avaSituation(reportData.grade_of_manageability) || "Keine Angabe",
    };
  } catch (error) {
    console.error("Error loading report:", error);
    return null;
  }
}

function transformExpo(expo) {
  if (expo == "all") {
    return ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  }
  if (expo == "N-NE") {
    return ["N", "NE"];
  }
  if (expo == "NE-E") {
    return ["NE", "E"];
  }
  if (expo == "E-SE") {
    return ["E", "SE"];
  }
  if (expo == "SE-S") {
    return ["SE", "S"];
  }
  if (expo == "S-SW") {
    return ["S", "SW"];
  }
  if (expo == "SW-W") {
    return ["SW", "W"];
  }
  if (expo == "W-NW") {
    return ["W", "NW"];
  }
  if (expo == "NW-N") {
    return ["NW", "N"];
  }
  if (expo == "N-E") {
    return ["N", "NE", "E"];
  }
  if (expo == "NE-SE") {
    return ["NE", "E", "SE"];
  }
  if (expo == "E-S") {
    return ["E", "SE", "S"];
  }
  if (expo == "SE-SW") {
    return ["SE", "S", "SW"];
  }
  if (expo == "S-W") {
    return ["S", "SW", "W"];
  }
  if (expo == "SW-NW") {
    return ["SW", "W", "NW"];
  }
  if (expo == "W-N") {
    return ["W", "NW", "N"];
  }
  if (expo == "NW-NE") {
    return ["NW", "N", "NE"];
  }
  if (expo == "N-S") {
    return ["N", "NE", "E", "SE", "S"];
  }
  if (expo == "NE-SW") {
    return ["NE", "E", "SE", "S", "SW"];
  }
  if (expo == "E-W") {
    return ["E", "SE", "S", "SW", "W"];
  }
  if (expo == "SE-NW") {
    return ["SE", "S", "SW", "W", "NW"];
  }
  if (expo == "S-N") {
    return ["S", "SW", "W", "NW", "N"];
  }
  if (expo == "SW-NE") {
    return ["SW", "W", "NW", "N", "NE"];
  }
  if (expo == "W-E") {
    return ["W", "NW", "N", "NE", "E"];
  }
  if (expo == "NW-SE") {
    return ["NW", "N", "NE", "E", "SE"];
  }
  return [expo];
}

function transformSnowEntries(snowData: any[]): SnowTypeEntry[] {
  if (!snowData || !Array.isArray(snowData)) return [];
  return snowData
    .filter((snow) => snow.snow_type != null && snow.snow_type !== "")
    .map((snow) => ({
      id: Math.random().toString(36).substring(2, 9), // Generate a random ID
      aspects:
        snow.expos?.flatMap((expo: any) => transformExpo(expo.expo)) ?? [],
      type: mapSnowType(snow.snow_type) || "",
      altitudeFrom: snow.from_altitude?.toString() || "",
      altitudeTo: snow.to_altitude?.toString() || "",
      drawMiddleLayer: snow.draw_middle_layer || false,
    }));
}

function transformAvalancheEntries(avalancheData: any[]): AvalancheTypeEntry[] {
  if (!avalancheData || !Array.isArray(avalancheData)) return [];
  return avalancheData
    .filter(
      (avalanche) => avalanche.problem != null && avalanche.problem !== ""
    )
    .map((avalanche) => ({
      id: Math.random().toString(36).substring(2, 9), // Generate a random ID
      aspects:
        avalanche.expos?.flatMap((expo: any) => transformExpo(expo.expo)) ?? [],
      type: mapAvaType(avalanche.problem) || "",
      altitudeFrom: avalanche.from_altitude?.toString() || "",
      altitudeTo: avalanche.to_altitude?.toString() || "",
      drawMiddleLayer: avalanche.draw_middle_layer || false,
    }));
}

async function transformImageEntries(
  imagesData: any[]
): Promise<ImageTypeEntry[]> {
  if (!imagesData || !Array.isArray(imagesData)) return [];

  const transformedEntries = await Promise.all(
    imagesData.map(async (image) => {
      let imageUrl;

      // If we have an image ID, fetch the image data from Strapi
      if (image.id) {
        imageUrl = image.picture?.url
          ? `https://backend.schnee.report${image.picture.url}`
          : undefined;
      }

      return {
        file: null, // We can't recreate the File object from the API
        description: image.caption || "",
        previewUrl: imageUrl,
        rawImage: image,
      };
    })
  );

  return transformedEntries;
}
